import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination, Image } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaTag, FaImage, FaSort, FaEye } from 'react-icons/fa';
import { categoriesAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Categories = () => {
    const [categories, setCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [apiBaseURL] = useState(import.meta.env.VITE_API_URL);
    const API_BASE_URL = import.meta.env.VITE_API_URL;
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        is_active: '1',
        photo: null,
        photoPreview: ''
    });

    useEffect(() => {
        fetchCategories();
    }, [pagination.page]);

    useEffect(() => {
        if (Array.isArray(categories)) {
            const filtered = categories.filter(category => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    category.name?.toLowerCase().includes(searchLower) ||
                    category.description?.toLowerCase().includes(searchLower) ||
                    category.slug?.toLowerCase().includes(searchLower)
                );
            });
            setFilteredCategories(filtered);
        }
    }, [searchTerm, categories]);

    const getImageUrl = (photoPath) => {
        if (!photoPath) return null;

        if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
            return photoPath;
        }

        if (photoPath.includes('/uploads/')) {
            return `${API_BASE_URL}${photoPath}`;
        }

        return `${API_BASE_URL}/public/uploads/categories/${photoPath}`;
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await categoriesAPI.list({
                page: pagination.page,
                limit: pagination.limit
            });

            console.log('Categories API Response:', response.data);

            let categoriesData = [];
            let paginationData = {};

            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    categoriesData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    categoriesData = response.data.data;
                }
                if (response.data.data.meta) {
                    paginationData = response.data.data.meta;
                }
            }

            if (!Array.isArray(categoriesData)) {
                console.error('Invalid categories data format:', response.data);
                categoriesData = [];
            }

            categoriesData = categoriesData.map(cat => {
                const imageUrl = getImageUrl(cat.photo || cat.photo_url);
                return {
                    ...cat,
                    photo_url: imageUrl,
                    photo_display: imageUrl,
                    status: cat.is_active === 1 ? 'active' : 'inactive'
                };
            });

            setCategories(categoriesData);
            setFilteredCategories(categoriesData);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || categoriesData.length,
                pages: paginationData.pages || Math.ceil((paginationData.total || categoriesData.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching categories:', err);
            setError(err.response?.data?.message || 'Failed to fetch categories');
            setCategories([]);
            setFilteredCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedCategory?.id) {
            setError('Category ID is missing');
            setShowDeleteModal(false);
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Deleting category with ID:', selectedCategory.id);

            const categoryId = selectedCategory.id;

            // Get the authentication token
            const token = localStorage.getItem('token') || sessionStorage.getItem('token') || 'your-token-here';

            // Make DELETE request with proper headers
            const response = await fetch(`${apiBaseURL}/categories/${categoryId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('Delete response status:', response.status);

            // Handle 204 No Content response
            if (response.status === 204) {
                setSuccessMessage('Category deleted successfully!');

                // Remove from state
                setCategories(prev => prev.filter(cat => cat.id !== categoryId));
                setFilteredCategories(prev => prev.filter(cat => cat.id !== categoryId));

                // Update pagination
                setPagination(prev => ({
                    ...prev,
                    total: prev.total - 1
                }));

                setShowDeleteModal(false);
                setSelectedCategory(null);
                setTimeout(() => setSuccessMessage(''), 3000);
                return;
            }

            // For other status codes, try to parse JSON
            const data = await response.json();
            console.log('Delete response data:', data);

            if (response.ok) {
                setSuccessMessage('Category deleted successfully!');

                // Remove from state
                setCategories(prev => prev.filter(cat => cat.id !== categoryId));
                setFilteredCategories(prev => prev.filter(cat => cat.id !== categoryId));

                // Update pagination
                setPagination(prev => ({
                    ...prev,
                    total: prev.total - 1
                }));

                setShowDeleteModal(false);
                setSelectedCategory(null);
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                // Handle backend error messages
                if (data && data.message) {
                    setError(data.message);
                } else if (response.status === 404) {
                    setError('Category not found');
                    // Remove from state anyway
                    setCategories(prev => prev.filter(cat => cat.id !== categoryId));
                    setFilteredCategories(prev => prev.filter(cat => cat.id !== categoryId));
                } else if (response.status === 403) {
                    setError('Permission denied');
                } else {
                    setError('Failed to delete category');
                }
            }

        } catch (err) {
            console.error('Error deleting category:', err);

            // Network error or JSON parsing error
            if (err instanceof SyntaxError) {
                // This might happen if we get a 204 response but try to parse JSON
                setError('Delete operation completed but server returned no content');
            } else {
                setError(err.message || 'Failed to delete category');
            }
        } finally {
            setActionLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            if (!formData.name || !formData.slug) {
                setError('Name and slug are required');
                return;
            }

            if (!formData.photo) {
                setError('Category photo is required');
                return;
            }

            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('slug', formData.slug);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('is_active', formData.is_active);
            formDataToSend.append('photo', formData.photo);

            const response = await categoriesAPI.create(formDataToSend);

            if (response.data.status === 'success') {
                setSuccessMessage('Category created successfully!');
                setShowCreateModal(false);
                resetForm();
                fetchCategories();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data.message || 'Failed to create category');
            }

        } catch (err) {
            console.error('Error creating category:', err);
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create category');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedCategory?.id) {
            setError('Category ID is missing');
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            if (!formData.name || !formData.slug) {
                setError('Name and slug are required');
                return;
            }

            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('slug', formData.slug);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('is_active', formData.is_active);

            if (formData.photo) {
                formDataToSend.append('photo', formData.photo);
            }

            const response = await categoriesAPI.update(selectedCategory.id, formDataToSend);

            if (response.data.status === 'success') {
                setSuccessMessage('Category updated successfully!');
                setShowEditModal(false);
                resetForm();
                setSelectedCategory(null);
                setIsEditing(false);
                fetchCategories();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data.message || 'Failed to update category');
            }

        } catch (err) {
            console.error('Error updating category:', err);
            let errorMsg = 'Failed to update category';
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            }
            setError(errorMsg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleStatusChange = async (categoryId, currentStatus) => {
        try {
            const category = categories.find(c => c.id === categoryId);
            if (!category) return;

            const newIsActive = category.is_active === 1 ? '0' : '1';
            const formData = new FormData();
            formData.append('is_active', newIsActive);

            await categoriesAPI.update(categoryId, formData);
            fetchCategories();
        } catch (err) {
            console.error('Error changing category status:', err);
            setError('Failed to change category status');
        }
    };

    const openEditModal = (category) => {
        setSelectedCategory(category);
        setFormData({
            name: category.name || '',
            slug: category.slug || '',
            description: category.description || '',
            is_active: category.is_active?.toString() || '1',
            photo: null,
            photoPreview: category.photo_display || category.photo_url || ''
        });
        setIsEditing(true);
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (category) => {
        console.log('Opening delete modal for category:', category);
        console.log('Category ID:', category?.id);
        console.log('Product count:', category?.product_count);
        console.log('Subcategory count:', category?.subcategory_count);

        setSelectedCategory(category);
        setShowDeleteModal(true);
        setError('');
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            description: '',
            is_active: '1',
            photo: null,
            photoPreview: ''
        });
        setIsEditing(false);
        setSelectedCategory(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please select an image file (JPEG, PNG, GIF, etc.)');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setError('Image size should be less than 5MB');
                return;
            }

            setFormData({
                ...formData,
                photo: file,
                photoPreview: URL.createObjectURL(file)
            });
            setError('');
        }
    };

    const getStatusBadge = (status, isActive) => {
        const isActiveBool = isActive === 1 || isActive === true || status === 'active';
        return isActiveBool ?
            <Badge bg="success">Active</Badge> :
            <Badge bg="secondary">Inactive</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const generateSlugFromName = (name) => {
        if (!name) return '';
        return name
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/--+/g, '-')
            .trim();
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading categories...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Categories Management</h1>
                    <p className="text-muted mb-0">Manage product categories and organization</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-2" />
                        Create Category
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <strong>Error:</strong> {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    <strong>Success:</strong> {successMessage}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search categories by name, slug or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{searchTerm ? filteredCategories.length : categories.length}</strong> categories
                                </small>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Clear Search
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th width="60">Image</th>
                                <th>Category</th>
                                <th>Slug</th>
                                <th>Description</th>
                                <th>Subcategories</th>
                                <th>Products</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategories.length > 0 ? (
                                filteredCategories.map(category => (
                                    <tr key={category.id}>
                                        <td>
                                            {category.photo_display || category.photo_url ? (
                                                <Image
                                                    src={category.photo_display || category.photo_url}
                                                    rounded
                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                    alt={category.name}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                                                        e.target.onerror = null;
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '50px', height: '50px' }}
                                                >
                                                    <FaTag className="text-white" size={20} />
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div>
                                                <div className="fw-semibold">{category.name || 'N/A'}</div>
                                                <small className="text-muted">
                                                    ID: {category.id?.substring(0, 12)}...
                                                </small>
                                            </div>
                                        </td>
                                        <td>
                                            <code className="small">{category.slug || 'N/A'}</code>
                                        </td>
                                        <td>
                                            <div className="small text-muted">
                                                {category.description ?
                                                    `${category.description.substring(0, 60)}${category.description.length > 60 ? '...' : ''}`
                                                    : 'No description'}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg="info">
                                                {category.subcategory_count || 0}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg="secondary">
                                                {category.product_count || 0}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getStatusBadge(category.status, category.is_active)}
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="ms-2 p-0"
                                                    onClick={() => handleStatusChange(category.id, category.status || category.is_active)}
                                                    title={`Change to ${category.is_active === 1 ? 'inactive' : 'active'}`}
                                                >
                                                    {category.is_active === 1 ? (
                                                        <FaEye className="text-danger" size={14} />
                                                    ) : (
                                                        <FaEye className="text-success" size={14} />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div>{formatDate(category.created_at)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openEditModal(category)}
                                                    title="Edit Category"
                                                    disabled={actionLoading}
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(category)}
                                                    title="Delete Category"
                                                    disabled={actionLoading}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No categories found matching "{searchTerm}"</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaTag className="h3 mb-3" />
                                                    <p>No categories found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {categories.length} of {pagination.total} categories
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {categories.length > 0 && (
                <Row className="mt-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-primary text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">{pagination.total}</div>
                                <small>Total Categories</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-success text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {categories.filter(c => c.is_active === 1).length}
                                </div>
                                <small>Active Categories</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-info text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {categories.reduce((total, cat) => total + (cat.subcategory_count || 0), 0)}
                                </div>
                                <small>Total Subcategories</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-warning text-dark">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {categories.reduce((total, cat) => total + (cat.product_count || 0), 0)}
                                </div>
                                <small>Total Products</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Category"
                onSubmit={handleCreate}
                submitText={actionLoading ? "Creating..." : "Create Category"}
                size="lg"
                loading={actionLoading}
            >
                {/* Create form content remains the same */}
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => {
                                    const newName = e.target.value;
                                    setFormData({
                                        ...formData,
                                        name: newName,
                                        slug: formData.slug || generateSlugFromName(newName)
                                    });
                                }}
                                required
                                placeholder="Enter category name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Slug *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                required
                                placeholder="Enter URL slug"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                URL-friendly version of the name (lowercase, hyphens)
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter category description"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                                disabled={actionLoading}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category Photo *</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                required={!isEditing}
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Upload a photo for the category (required, max 5MB)
                            </Form.Text>
                            {formData.photoPreview && (
                                <div className="mt-3">
                                    <Image
                                        src={formData.photoPreview}
                                        rounded
                                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                        alt="Preview"
                                    />
                                    <div className="small text-muted mt-1">New photo preview</div>
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit Category: ${selectedCategory?.name || ''}`}
                onSubmit={handleUpdate}
                submitText={actionLoading ? "Updating..." : "Update Category"}
                size="lg"
                loading={actionLoading}
            >
                {/* Edit form content remains the same */}
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter category name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Slug *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                required
                                placeholder="Enter URL slug"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                URL-friendly version of the name (lowercase, hyphens)
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter category description"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                                disabled={actionLoading}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category Photo</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Upload a new photo or leave empty to keep current
                            </Form.Text>
                            {formData.photoPreview && (
                                <div className="mt-3">
                                    <Image
                                        src={formData.photoPreview}
                                        rounded
                                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                        alt="New Preview"
                                    />
                                    <div className="small text-muted mt-1">New photo preview</div>
                                </div>
                            )}
                            {!formData.photoPreview && selectedCategory?.photo_display && (
                                <div className="mt-3">
                                    <Image
                                        src={selectedCategory.photo_display}
                                        rounded
                                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                        alt="Current"
                                    />
                                    <div className="small text-muted mt-1">Current photo</div>
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Category"
                message={
                    <div>
                        <p>Are you sure you want to delete category <strong>"{selectedCategory?.name}"</strong>?</p>
                        <div className="alert alert-warning small">
                            <strong>Note:</strong> You cannot delete a category that has:
                            <ul className="mb-0">
                                <li>Associated products: <strong>{selectedCategory?.product_count || 0}</strong></li>
                                <li>Associated subcategories: <strong>{selectedCategory?.subcategory_count || 0}</strong></li>
                            </ul>
                        </div>
                        <p className="text-danger mb-0 mt-2">
                            This action cannot be undone.
                        </p>
                        <p className="text-muted small mt-2 mb-0">
                            Category ID: <code>{selectedCategory?.id}</code>
                        </p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading || (selectedCategory?.product_count > 0) || (selectedCategory?.subcategory_count > 0)}
                variant="danger"
                loading={actionLoading}
            />
        </div>
    );
};

export default Categories;